// Step 11 — share creation + public share landing page.
//
// PUT-first upload flow (mirrors artworks): POST /api/shares creates the row
// and hands back an R2 presigned PUT URL the client uses to upload the
// 1080×1080 share-card PNG. GET /api/shares/:id is public — no auth — and
// returns a 7-day presigned GET for both the user's hero artwork and the
// composed share card so the SPA can render the public share page from a
// single fetch.

import { z } from 'zod'
import { packIdSchema } from './api.ts'

export const shareCreateRequestSchema = z
  .object({
    artworkId: z.string().min(1),
  })
  .strict()
export type ShareCreateRequest = z.infer<typeof shareCreateRequestSchema>

export const shareCreateResponseSchema = z
  .object({
    shareId: z.string().min(1),
    cardPutUrl: z.string().url(),
    cardKey: z.string().min(1),
    /** Epoch ms after which cardPutUrl is no longer signable. ~5 minutes. */
    urlsExpireAt: z.number().int().nonnegative(),
    shareUrl: z.string().url(),
    shareTextZh: z.string().min(1),
  })
  .strict()
export type ShareCreateResponse = z.infer<typeof shareCreateResponseSchema>

const sharePackSchema = z
  .object({
    id: packIdSchema,
    nameZh: z.string().min(1),
  })
  .strict()

const shareItemSchema = z
  .object({
    id: z.string().min(1),
    nameZh: z.string().min(1),
    nameEn: z.string().min(1),
    scriptureRef: z.string().min(1),
    descriptionZh: z.string(),
  })
  .strict()

export const sharePublicResponseSchema = z
  .object({
    shareId: z.string().min(1),
    displayName: z.string().min(1),
    churchName: z.string().nullable(),
    /** Epoch ms when the share row was created. */
    createdAt: z.number().int().nonnegative(),
    /** Post-increment count, including the caller's own view. */
    viewCount: z.number().int().nonnegative(),
    pack: sharePackSchema,
    item: shareItemSchema,
    /** 7-day presigned GET for the user's colored artwork PNG (R2). */
    heroGetUrl: z.string().url(),
    /** 7-day presigned GET for the 1080×1080 composed share-card PNG (R2). */
    cardImageGetUrl: z.string().url(),
  })
  .strict()
export type SharePublicResponse = z.infer<typeof sharePublicResponseSchema>
